<template>
    <el-form ref="loginFormRef" :model="loginForm" :rules="loginRules" class="login-form">
        <el-form-item prop="phone" class="mb-16">
            <el-input size="large" v-model="loginForm.phone" placeholder="请输入手机号" maxlength="20" name="phone">
                <template #prefix><i class="iconfont icon-Frame2"></i></template>
            </el-input>
        </el-form-item>
        <el-form-item prop="code">
            <div class="phone-code">
                <el-input
                    class="phone-code-input"
                    size="large"
                    placeholder="验证码"
                    v-model="loginForm.code"
                    maxlength="10"
                    @keyup.enter="clickLoginBtn(loginFormRef)"
                >
                    <template #prefix><i class="iconfont icon-code"></i></template>
                </el-input>
                <div class="code-position">
                    <span
                        id="captcha-button"
                        v-if="countDown < 1"
                        class="code-btn"
                        v-loading="loading"
                        @click.stop="onClickCheckPhone"
                    >
                        发送验证码
                    </span>
                    <span v-else class="count-down">{{ countDown }}秒重新获取</span>
                </div>
            </div>
        </el-form-item>
    </el-form>
    <el-button
        class="submit-btn"
        type="primary"
        @click="clickLoginBtn(loginFormRef)"
        :loading="btnLoading"
        :disabled="isSubmitDisabled"
        >登录</el-button
    >
    <div v-show="showCaptchaWrap" class="captcha-wrap">
        <div id="captcha-element" @click.stop />
    </div>
</template>

<script setup lang="ts">
import { ElForm, ElFormItem, ElInput, ElButton, FormInstance, ElMessage } from "element-plus";
import { ref, type Ref, reactive, computed, watch } from "vue";
import { messageService } from "@/api/services/messageService";
import { loginService } from "@/api/services/loginService";
import { customerLoginService } from "@/api/services/customerLoginService.ts";
import { customerMessageService } from "@/api/services/customerMessageService.ts";
import { ValidatePhone } from "@/entity/login";
import moment from "moment/moment";
import { useUserStore } from "@/stores/modules/user";
import { SYSTEM_LOGIN_TYPE, isApiError } from "../type.ts";
import { type CaptchaType, useCaptcha } from "@/views/pages/login/hooks";

const emits = defineEmits(["changeLoadingState", "getToken", "verifyIdentity", "isSelectAgreements"]);
const props = defineProps<{
    deviceCode: string;
    systemType: number;
    isChecked: boolean;
}>();

const loginForm = ref<Partial<ValidatePhone>>({});

const btnLoading = ref<boolean>(false);
const isSubmitDisabled = computed(() => {
    return !(loginForm.value.phone && loginForm.value.code);
});

const showCaptchaWrap = ref(false);
const userStore = useUserStore();
const randomNum = ref("");
const captchaVerifyCallback = async (captchaVerifyParam: string) => {
    if (!showCaptchaWrap.value) return;
    randomNum.value = `${moment(new Date()).valueOf()}${Math.random() * 1000000000}`;
    const params = {
        serialNumber: randomNum.value as string,
        SceneId: "1wjppc2z",
        captchaVerifyParam
    };
    try {
        let res = undefined;
        if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
            res = await messageService.validateCaptcha(params);
        } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
            res = await customerMessageService.validateCaptcha(params);
        }
        if (res?.data) {
            await onClickSendCode(randomNum.value);
        } else {
            captcha.value!.refresh();
        }
    } catch (e) {
        console.log(e);
        captcha.value!.refresh();
    }
};

// 验证码
let captcha: Ref<CaptchaType | undefined>;
captcha = useCaptcha({ captchaVerifyCallback });
const onClickCheckPhone = async () => {
    captcha?.value!.refresh();
    await loginFormRef.value!.validateField("phone");
    showCaptchaWrap.value = true;
    captcha?.value!.show();
};

const countDown: Ref<number> = ref(0);
const loading = ref(false);
const onClickSendCode = async (serialNumber: string) => {
    try {
        loading.value = true;
        if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
            await messageService.sendPhoneCode(loginForm.value.phone!);
        } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
            await customerMessageService.sendPhoneCode(loginForm.value.phone!, serialNumber);
        }
        closeCaptchaWrap();
        loginForm.value.code = undefined;
        loading.value = false;
        countDown.value = 60;
        setCodeCountDown();
    } catch (e) {
        loading.value = false;
        closeCaptchaWrap();
        console.log(e);
    }
};

const setCodeCountDown = (): void => {
    const timer = setInterval(() => {
        countDown.value -= 1;
        if (countDown.value === 0) {
            clearInterval(timer);
        }
    }, 1000);
};

const phoneRuleCallback = (_: unknown, value: string, callback: (error?: Error) => void) => {
    // 校验是否是手机号或者邮箱
    const reg = /^1[3456789]\d{9}$/;
    if (reg.test(value)) {
        callback();
    } else {
        callback(new Error("请输入正确的手机号"));
    }
};
const loginRules = reactive({
    phone: [
        { required: true, message: "请输入手机号", trigger: "blur" },
        { validator: phoneRuleCallback, trigger: "blur" }
    ],
    code: [{ required: true, message: "请输入验证码", trigger: "blur" }]
});
const loginFormRef = ref<FormInstance>();
const clickLoginBtn = (formEl: FormInstance | undefined) => {
    formEl?.validate(async valid => {
        if (valid) {
            try {
                if (!props.isChecked) {
                    emits("isSelectAgreements");
                    return;
                }
                btnLoading.value = true;
                if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
                    await adminLogin();
                } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
                    await captchaLogin();
                }
                btnLoading.value = false;
            } catch (err: unknown) {
                btnLoading.value = false;
                // 类型安全的错误处理
                if (isApiError(err) && err.response?.data?.code === "30011002") {
                    emits("verifyIdentity", loginForm.value.phone);
                }
            }
        }
    });
};
//供应链端验证码登录
const adminLogin = async () => {
    const params = {
        phone: loginForm.value.phone!,
        code: loginForm.value.code!,
        deviceCode: props.deviceCode
    };
    const data = await loginService.loginPhone(params);
    emits("getToken", data.accessToken);
    userStore?.setMobile(params.phone);
};

const closeCaptchaWrap = () => {
    showCaptchaWrap.value = false;
    captcha.value?.hide();
};
//客户端验证码登录
const captchaLogin = async () => {
    const data = await customerLoginService.login({
        mobile: loginForm.value.phone!,
        captcha: loginForm.value.code! as string,
        deviceCode: props.deviceCode
    });
    emits("getToken", data.accessToken);
    userStore?.setMobile(loginForm.value.phone as string);
    if (!data) {
        ElMessage.error("登录失败，请重新登录！");
        return;
    }
};
watch(
    () => props.systemType,
    () => {
        captcha.value!.refresh();
    }
);
defineExpose({
    closeCaptchaWrap
});
</script>

<style scoped lang="scss">
$primary-color: #1c5bff;
.login-btn {
    width: 100%;
}
.login-client {
    position: relative;
    top: -12px;
    color: #2143fc;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    text-transform: capitalize;
}
:deep() {
    .phone-code {
        position: relative;
        width: 100%;
        &-input {
            width: 100%;
            .el-input__inner {
                padding-right: 80px;
            }
        }
        .code-position {
            position: absolute;
            top: 4px;
            right: 0;
            width: fit-content;
            .code-btn {
                cursor: pointer;
                color: $primary-color;
                text-align: center;
                padding: 6px 16px;
                border-radius: 6px;
                line-height: 14px;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
            }
            .count-down {
                position: relative;
                right: 16px;
                color: #cccccc;
            }
        }
    }
    .el-checkbox {
        .el-checkbox__label {
            font-size: 12px;
            color: #999999;
        }
    }
}
.mt-80 {
    margin-top: 80px;
}
.protocol {
    color: $primary-color;
    text-decoration: none;
}

.captcha-wrap {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.login-form {
    :deep(.el-input) {
        --el-border-radius-base: 6px;
        .el-input__prefix-inner > :last-child {
            margin-right: 12px;
        }
    }
}
.submit-btn {
    height: 40px;
    width: 100%;
    color: var(--el-color-button-text-color);
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    padding: 17px 0;
    border-radius: 6px;
    margin-top: 12px;
    :deep(span) {
        color: #ffffff;
    }
}
</style>
